import { Tag } from "antd";
import { Link } from "react-router-dom";
import { formatDate } from "../utils";

// ---------------------------------------------------------------------------
// Shared cell renderers — kept inline to avoid pulling in another file just
// for two helpers. `dash` is the tabular convention used by AdminOrganizations:
// render "—" for null/empty, never raw `null` / `undefined`.
// ---------------------------------------------------------------------------

const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);

// Org name → admin org detail (mirrors AdminUsers/AdminSubscriptions pattern).
const orgLinkCell = (name, row) =>
  row?.organization_id ? (
    <Link to={`/admin/organizations/${row.organization_id}`}>{dash(name)}</Link>
  ) : (
    dash(name)
  );

// Client name → admin client detail. Portal slug is hard-wired per column
// block (one factory call inside each per-portal columns array) so each
// portal's notices table links to its own clients/:id detail page.
const clientLinkCell = (portalSlug) => (name, row) =>
  row?.client_id ? (
    <Link to={`/admin/${portalSlug}/clients/${row.client_id}`}>{dash(name)}</Link>
  ) : (
    dash(name)
  );

const yesNoTag = (v) =>
  v ? <Tag color="orange">Yes</Tag> : <Tag color="default">No</Tag>;

// Many-to-many assignees. Renders the `assigned_users` array (objects of
// {user_id, user_name, user_email}) as a row of tags; an empty array is
// the Shared pool, shown as a muted "Shared" tag.
const assignedUsersCell = (assignedUsers) => {
  const list = Array.isArray(assignedUsers) ? assignedUsers : [];
  if (list.length === 0) return <Tag color="default">Shared</Tag>;
  return (
    <span className="flex flex-wrap gap-1">
      {list.map((u) => (
        <Tag color="blue" key={u.user_id}>
          {u.user_name || u.user_email || u.user_id}
        </Tag>
      ))}
    </span>
  );
};

// Some upstream flows have leaked timestamps / pure-numeric junk into
// `response_status` / `notice_status` columns (e.g. "2026-03-21
// 15:46:30.756543", "0.000000000"). Detect those and render "—" so
// the table stays readable. Real status values are short alpha
// strings like "Pending" / "Submitted" / "Open".
const _looksLikeJunkStatus = (s) => {
  if (typeof s !== "string") return false;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return true;       // ISO-ish date
  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) return true;     // dd/mm/yyyy
  if (/^[\d.\s:-]+$/.test(s.trim())) return true;      // pure numeric / time
  return false;
};

const statusTag = (v) => {
  if (!v || _looksLikeJunkStatus(v)) return "-";
  const map = {
    Pending: "gold",
    "Response Generated": "blue",
    "Response Finalized": "geekblue",
    Submitted: "green",
    Open: "blue",
    Close: "default",
    Closed: "default",
  };
  return <Tag color={map[v] || "default"}>{v}</Tag>;
};

const numberCell = (v) =>
  v === null || v === undefined ? "-" : Number(v).toLocaleString("en-IN");

// ---------------------------------------------------------------------------
// GST clients — matches the field list Hemant sir specified (client_name,
// gstin, gst_username, disabled, last_sync, is_processing, created/updated,
// organization_id→name, assigned_user_id→name, partner_client_ref).
// ---------------------------------------------------------------------------

export const GST_CLIENTS_COLUMNS = [
  {
    title: "Client Name",
    dataIndex: "client_name",
    key: "client_name",
    fixed: "left",
    width: 180,
    render: dash,
  },
  { title: "GSTIN", dataIndex: "gstin", key: "gstin", render: dash },
  {
    title: "GST Username",
    dataIndex: "gst_username",
    key: "gst_username",
    render: dash,
  },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  {
    title: "Assigned User",
    dataIndex: "assigned_users",
    key: "assigned_users",
    render: assignedUsersCell,
  },
  {
    title: "Partner Client Ref",
    dataIndex: "partner_client_ref",
    key: "partner_client_ref",
    render: dash,
  },
  {
    title: "Disabled",
    dataIndex: "disabled",
    key: "disabled",
    render: yesNoTag,
  },
  {
    title: "Processing",
    dataIndex: "is_processing",
    key: "is_processing",
    render: yesNoTag,
  },
  {
    title: "Last Sync",
    dataIndex: "last_gst_sync",
    key: "last_gst_sync",
    render: (v) => formatDate(v),
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
  {
    title: "Updated",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (v) => formatDate(v),
  },
];

// ---------------------------------------------------------------------------
// Income Tax clients
// ---------------------------------------------------------------------------

export const IT_CLIENTS_COLUMNS = [
  {
    title: "Client Name",
    dataIndex: "client_name",
    key: "client_name",
    fixed: "left",
    width: 180,
    render: dash,
  },
  {
    title: "PAN / TAN",
    dataIndex: "username",
    key: "username",
    render: dash,
  },
  { title: "DOB", dataIndex: "dob", key: "dob", render: (v) => formatDate(v) },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  {
    title: "Assigned User",
    dataIndex: "assigned_users",
    key: "assigned_users",
    render: assignedUsersCell,
  },
  {
    title: "Partner Client Ref",
    dataIndex: "partner_client_ref",
    key: "partner_client_ref",
    render: dash,
  },
  {
    title: "Disabled",
    dataIndex: "disabled",
    key: "disabled",
    render: yesNoTag,
  },
  {
    title: "Processing",
    dataIndex: "is_processing",
    key: "is_processing",
    render: yesNoTag,
  },
  {
    title: "Notice",
    dataIndex: "notice_counts",
    key: "notice_counts",
    width: 140,
    render: (counts) => {
      const n = counts || { eproc: 0, rtod: 0, total: 0 };
      return (
        <div style={{ lineHeight: 1.25 }}>
          <div style={{ fontWeight: 600 }}>Total: {n.total}</div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            E-Proceeding: {n.eproc}
          </div>
          <div style={{ fontSize: 11, color: "#666" }}>
            R. Outstanding: {n.rtod}
          </div>
        </div>
      );
    },
  },
  {
    title: "Last Sync",
    dataIndex: "last_income_tax_sync",
    key: "last_income_tax_sync",
    render: (v) => formatDate(v),
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
  {
    title: "Updated",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (v) => formatDate(v),
  },
];

// ---------------------------------------------------------------------------
// TDS clients
// ---------------------------------------------------------------------------

export const TDS_CLIENTS_COLUMNS = [
  {
    title: "Client Name",
    dataIndex: "client_name",
    key: "client_name",
    fixed: "left",
    width: 180,
    render: dash,
  },
  {
    title: "TAN",
    dataIndex: "tan_number",
    key: "tan_number",
    render: dash,
  },
  {
    title: "TDS Username",
    dataIndex: "tds_username",
    key: "tds_username",
    render: dash,
  },
  { title: "DOB", dataIndex: "dob", key: "dob", render: (v) => formatDate(v) },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  {
    title: "Assigned User",
    dataIndex: "assigned_users",
    key: "assigned_users",
    render: assignedUsersCell,
  },
  {
    title: "Partner Client Ref",
    dataIndex: "partner_client_ref",
    key: "partner_client_ref",
    render: dash,
  },
  {
    title: "Disabled",
    dataIndex: "disabled",
    key: "disabled",
    render: yesNoTag,
  },
  {
    title: "Processing",
    dataIndex: "is_processing",
    key: "is_processing",
    render: yesNoTag,
  },
  {
    title: "Last Sync",
    dataIndex: "last_tds_sync",
    key: "last_tds_sync",
    render: (v) => formatDate(v),
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
  {
    title: "Updated",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (v) => formatDate(v),
  },
];

// ---------------------------------------------------------------------------
// GST notices & orders
// ---------------------------------------------------------------------------

// Sequence per Hemant sir's R7 spec — exactly these 8 columns shown
// in the table; everything else (Doc ID, Issued By, Issue Date, Due
// Date, Amount, Notice Status) was dropped from the UI.
export const GST_NOTICES_COLUMNS = [
  {
    title: "Notice/Demand Order ID",
    dataIndex: "ref_id",
    key: "ref_id",
    fixed: "left",
    width: 220,
    render: dash,
  },
  {
    title: "Client",
    dataIndex: "client_name",
    key: "client_name",
    render: clientLinkCell("gst"),
  },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  { title: "Type", dataIndex: "type", key: "type", render: dash },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: 240,
    render: dash,
  },
  {
    title: "Disabled",
    dataIndex: "disabled",
    key: "disabled",
    render: yesNoTag,
  },
  {
    title: "Response Status",
    dataIndex: "response_status",
    key: "response_status",
    render: statusTag,
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
];

// ---------------------------------------------------------------------------
// GST additional notices
// ---------------------------------------------------------------------------

// Same 8-column shape as GST_NOTICES_COLUMNS per Hemant sir's R7 spec.
// Dropped from the UI table: ARN, Notice Status, Issue Date, Due Date.
export const GST_ADDITIONAL_NOTICES_COLUMNS = [
  {
    title: "Notice/Demand Order ID",
    dataIndex: "ref_id",
    key: "ref_id",
    fixed: "left",
    width: 220,
    render: dash,
  },
  {
    title: "Client",
    dataIndex: "client_name",
    key: "client_name",
    render: clientLinkCell("gst"),
  },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  { title: "Type", dataIndex: "type", key: "type", render: dash },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: 240,
    render: dash,
  },
  {
    title: "Disabled",
    dataIndex: "disabled",
    key: "disabled",
    render: yesNoTag,
  },
  {
    title: "Response Status",
    dataIndex: "response_status",
    key: "response_status",
    render: statusTag,
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
];

// ---------------------------------------------------------------------------
// Income Tax — E-Proceedings
// ---------------------------------------------------------------------------

// Sequence per Hemant sir's R8 spec — exactly these 8 columns for IT
// E-Proceedings; everything else (PAN, DIN, Section, FY, AY,
// Proceeding Status, Notice Sent) was dropped from the UI.
//
// "Notice/Demand Order ID" maps to `notice_din` (the government's
// Document Identification Number — the actual notice ID), NOT
// `proceeding_name` which is a free-text title.
export const IT_EPROCEEDINGS_COLUMNS = [
  {
    title: "Notice/Demand Order ID",
    dataIndex: "notice_din",
    key: "notice_din",
    fixed: "left",
    width: 220,
    render: dash,
  },
  {
    title: "Client",
    dataIndex: "client_name",
    key: "client_name",
    render: clientLinkCell("income-tax"),
  },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  {
    title: "Type",
    dataIndex: "proceeding_type",
    key: "proceeding_type",
    render: dash,
  },
  {
    title: "Disabled",
    dataIndex: "disabled",
    key: "disabled",
    render: yesNoTag,
  },
  {
    title: "Response Status",
    dataIndex: "response_status",
    key: "response_status",
    render: statusTag,
  },
  {
    title: "Due Date",
    dataIndex: "response_due_date",
    key: "response_due_date",
    render: (v) => formatDate(v),
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
];

// ---------------------------------------------------------------------------
// Income Tax — Response to Outstanding Demand
// ---------------------------------------------------------------------------

// Same 8-column sequence as IT_EPROCEEDINGS_COLUMNS per Hemant sir's
// R8 spec. Mapping for ROD-specific fields:
//   "Notice/Demand Order ID" → demand_reference_no
//   "Type"                   → response_type
//   "Due Date"               → date_of_service_notice (best stand-in;
//                              ROD has no native due_date — service
//                              notice is when the response clock starts)
//
// Dropped: Section, AY, Outstanding Amount, Notice Status, Mode of
// Service, Demand Raised, Notice Served, Response Submitted.
export const IT_ROD_COLUMNS = [
  {
    title: "Notice/Demand Order ID",
    dataIndex: "demand_reference_no",
    key: "demand_reference_no",
    fixed: "left",
    width: 220,
    render: dash,
  },
  {
    title: "Client",
    dataIndex: "client_name",
    key: "client_name",
    render: clientLinkCell("income-tax"),
  },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  {
    title: "Type",
    dataIndex: "response_type",
    key: "response_type",
    render: dash,
  },
  {
    title: "Disabled",
    dataIndex: "disabled",
    key: "disabled",
    render: yesNoTag,
  },
  {
    title: "Response Status",
    dataIndex: "response_status",
    key: "response_status",
    render: statusTag,
  },
  {
    title: "Due Date",
    dataIndex: "date_of_service_notice",
    key: "date_of_service_notice",
    render: (v) => formatDate(v),
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
];

// ---------------------------------------------------------------------------
// TDS / TCS — Notices (financial-year grouped)
// ---------------------------------------------------------------------------

// Sequence per Hemant sir's R9 spec — exactly these 5 columns; the
// Manual Demand / Processed Demand / Updated columns were dropped.
export const TDS_NOTICES_COLUMNS = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    fixed: "left",
    width: 240,
    render: dash,
  },
  {
    title: "Client",
    dataIndex: "client_name",
    key: "client_name",
    render: clientLinkCell("tds-tcs"),
  },
  {
    title: "Organization",
    dataIndex: "organization_name",
    key: "organization_name",
    render: orgLinkCell,
  },
  {
    title: "Financial Year",
    dataIndex: "financial_year",
    key: "financial_year",
    render: dash,
  },
  {
    title: "Created",
    dataIndex: "created_at",
    key: "created_at",
    render: (v) => formatDate(v),
  },
];